// @ts-nocheck


import { useState, useEffect, useCallback, useRef } from "react";
import { useDocumentStore, useUIStore } from "@/stores";
import { api } from "@/lib/api-client";
import { Editor } from "@/components/editor/BlockNoteEditor";
import { ShareDialog } from "./ShareDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  MoreHorizontal,
  Star,
  Share2,
  Download,
  Trash2,
  Archive,
  ChevronLeft,
  Clock,
} from "lucide-react";
import { formatRelativeTime } from "@/lib/utils";
import { toast } from "sonner";
import { motion } from "framer-motion";

export function DocumentView() {
  const { currentDocument, updateDocument, documents } = useDocumentStore();
  const { sidebarOpen, toggleSidebar } = useUIStore();
  const [title, setTitle] = useState("");
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Refs para mantener referencias actualizadas
  const currentDocRef = useRef(currentDocument);
  const updateDocRef = useRef(updateDocument);
  
  useEffect(() => {
    currentDocRef.current = currentDocument;
    updateDocRef.current = updateDocument;
  }, [currentDocument, updateDocument]);

  // Update title when document changes
  useEffect(() => {
    if (currentDocument) {
      setTitle(currentDocument.title);
    }
  }, [currentDocument]);

  // Save document - usando refs para evitar stale closures
  const saveDocument = useCallback(async (content: string) => {
    const doc = currentDocRef.current;
    if (!doc) {
      console.log("[DocumentView] No current document to save");
      return;
    }

    console.log("[DocumentView] Saving document:", doc.id, "title:", doc.title);
    setIsSaving(true);
    try {
      const data = await api.put<{ success: boolean; document: any }>('/documents', {
        id: doc.id,
        content,
      });
      if (data.success) {
        console.log("[DocumentView] Document saved successfully");
        updateDocRef.current(doc.id, { content, updatedAt: new Date() });
        setHasUnsavedChanges(false);
      } else {
        console.error("[DocumentView] Save failed:", data);
      }
    } catch (error) {
      console.error("[DocumentView] Save error:", error);
      toast.error("Error al guardar el documento");
    } finally {
      setIsSaving(false);
    }
  }, []);

  // Save title
  const saveTitle = async () => {
    if (!currentDocument || title === currentDocument.title) return;

    try {
      const data = await api.put<{ success: boolean; document: any }>('/documents', {
        id: currentDocument.id,
        title,
      });
      if (data.success) {
        updateDocument(currentDocument.id, { title });
        toast.success("Título actualizado");
      }
    } catch (error) {
      toast.error("Error al guardar título");
    }
    setIsEditingTitle(false);
  };

  // Toggle favorite
  const toggleFavorite = async () => {
    if (!currentDocument) return;

    try {
      const data = await api.put<{ success: boolean; document: any }>('/documents', {
        id: currentDocument.id,
        isFavorite: !currentDocument.isFavorite,
      });
      if (data.success) {
        updateDocument(currentDocument.id, { isFavorite: !currentDocument.isFavorite });
        toast.success(currentDocument.isFavorite ? "Eliminado de favoritos" : "Añadido a favoritos");
      }
    } catch (error) {
      toast.error("Error al actualizar");
    }
  };

  // Share document
  const shareDocument = async () => {
    if (!currentDocument) return;

    setShowShareDialog(true);
  };

  // Archive document
  const archiveDocument = async () => {
    if (!currentDocument) return;

    try {
      const data = await api.put<{ success: boolean; document: any }>('/documents', {
        id: currentDocument.id,
        isArchived: true,
      });
      if (data.success) {
        updateDocument(currentDocument.id, { isArchived: true });
        toast.success("Documento archivado");
      }
    } catch (error) {
      toast.error("Error al archivar");
    }
  };

  // Delete document
  const deleteDocument = async () => {
    if (!currentDocument) return;

    try {
      const data = await api.delete<{ success: boolean }>(`/documents?id=${currentDocument.id}`);
      if (data.success) {
        // Find parent or root document to select next
        const parentId = currentDocument.parentId;
        const nextDoc = documents.find((d) => d.id === parentId) || documents.find((d) => d.id !== currentDocument.id);
        
        toast.success("Documento eliminado");
        window.location.reload();
      }
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  // No document selected
  if (!currentDocument) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">📝</div>
          <h2 className="text-2xl font-semibold mb-2">Selecciona un documento</h2>
          <p className="text-muted-foreground">
            O crea uno nuevo desde la barra lateral
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-auto">
      {/* Header */}
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 shrink-0">
        <div className="flex items-center justify-between px-4 py-2">
          <div className="flex items-center gap-2">
            {!sidebarOpen && (
              <Button variant="ghost" size="sm" onClick={toggleSidebar}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
            )}

            {/* Document icon & title */}
            <div className="flex items-center gap-2">
              <span className="text-xl cursor-pointer hover:bg-accent rounded p-1">
                {currentDocument.icon || "📄"}
              </span>

              {isEditingTitle ? (
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  onBlur={saveTitle}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") saveTitle();
                    if (e.key === "Escape") {
                      setTitle(currentDocument.title);
                      setIsEditingTitle(false);
                    }
                  }}
                  className="h-8 text-lg font-semibold border-0 focus-visible:ring-0 p-0"
                  autoFocus
                />
              ) : (
                <h1
                  className="text-lg font-semibold cursor-pointer hover:bg-accent px-1 rounded"
                  onClick={() => setIsEditingTitle(true)}
                >
                  {currentDocument.title}
                </h1>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Status */}
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              {isSaving && (
                <Badge variant="secondary" className="animate-pulse">
                  Guardando...
                </Badge>
              )}
              <div className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {formatRelativeTime(currentDocument.updatedAt)}
              </div>
            </div>

            {/* Favorite button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleFavorite}
              className={currentDocument.isFavorite ? "text-yellow-500" : ""}
            >
              <Star className={`w-4 h-4 ${currentDocument.isFavorite ? "fill-current" : ""}`} />
            </Button>

            {/* Share button */}
            <Button variant="ghost" size="sm" onClick={shareDocument}>
              <Share2 className="w-4 h-4" />
            </Button>

            {/* More options */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm">
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => toast.info("Exportando...")}>
                  <Download className="w-4 h-4 mr-2" />
                  Exportar como Markdown
                </DropdownMenuItem>
                <DropdownMenuItem onClick={archiveDocument}>
                  <Archive className="w-4 h-4 mr-2" />
                  Archivar
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={deleteDocument} className="text-destructive">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Eliminar
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Tags */}
        {currentDocument.tags && currentDocument.tags.length > 0 && (
          <div className="flex gap-1 px-4 py-1">
            {currentDocument.tags.map((dt) => (
              <Badge key={dt.tagId} variant="secondary" className="text-xs">
                #{dt.tag.name}
              </Badge>
            ))}
          </div>
        )}
      </header>

      {/* Editor */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-4xl mx-auto py-8 px-4 pb-20">
          <div className="bg-white dark:bg-background rounded-lg shadow-sm border p-6 min-h-[calc(100vh-200px)]">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Editor
                key={currentDocument.id}
                initialContent={currentDocument.content}
                onSave={saveDocument}
                onChange={() => setHasUnsavedChanges(true)}
              />
            </motion.div>
          </div>
        </div>
      </div>

      {/* Share dialog */}
      <ShareDialog
        open={showShareDialog}
        onOpenChange={setShowShareDialog}
        documentId={currentDocument.id}
        documentTitle={currentDocument.title}
      />
    </div>
  );
}
